import React, { useEffect, useState } from "react";
import { db, storage, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { formatCurrencyUY, formatCurrencyUSD } from "../../utils/formatters";
import { tolerantId } from "../../utils/firestoreHelpers";
import {
  FaExclamationCircle,
  FaPlus,
  FaCheck,
  FaBan,
  FaSearch,
  FaHandHoldingUsd,
  FaMoneyBillWave,
  FaPen,
  FaPaperclip,
  FaSyncAlt,
} from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, inputBase, pageTitle } from "../../utils/designTokens";
import { getAllActiveFunds } from "../../utils/fundHelpers";
import { ingresosDeAlquiler } from "../../utils/fundBalances";
import { alertasDeAlquiler } from "../../utils/alquileresEdificio";
import StatusBadge from "../../components/StatusBadge";
import LoadingState from "../../components/LoadingState";
import { useAppDialog } from "../../components/AppDialog";

const INCOME_TYPES = [
  { id: "publicidad", label: "Cartel publicitario" },
  // Las unidades "Alquiler del edificio" con contrato se cobran solas por Cobranza;
  // este tipo queda para alquileres sin contrato en la plataforma.
  { id: "alquiler", label: "Alquiler (manual, sin contrato)" },
  { id: "antena", label: "Antena / telecomunicaciones" },
  { id: "otro", label: "Otro" },
];

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const IngresosEdificio = () => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const { selectedBuilding } = useBuilding();
  const buildingId = selectedBuilding?.buildingid;
  // Contratos de "Alquiler del edificio" y lo cobrado de ellos por Cobranza
  // (solo lectura: el ingreso ya lo cuentan los fondos desde el pago).
  const [contratos, setContratos] = useState([]);
  const [alquileresCobrados, setAlquileresCobrados] = useState([]);

  const now = new Date();
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("todos");
  const [search, setSearch] = useState("");

  const [annulModal, setAnnulModal] = useState(null);
  const [annulReason, setAnnulReason] = useState("");
  const [cobrarModal, setCobrarModal] = useState(null);
  const [cobrarDate, setCobrarDate] = useState("");
  const [editModal, setEditModal] = useState(null);

  const emptyForm = {
    concept: "",
    type: "publicidad",
    counterparty: "",
    amount: "",
    currency: "UYU",
    targetFundId: "caja",
    date: now.toISOString().split("T")[0],
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    recurring: false,
    alreadyReceived: true,
    receipt: null,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!buildingId) return;
    fetchIncomes();
    fetchAlquileres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // Contratos de alquiler del edificio y, si hay, los alquileres cobrados por
  // Cobranza. Si algo falla, la pantalla sigue con los ingresos manuales.
  const fetchAlquileres = async () => {
    try {
      const snap = await getDocs(query(collection(db, "alquileres"), where("buildingid", "in", tolerantId(buildingId))));
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setContratos(lista);
      if (!lista.length) {
        setAlquileresCobrados([]);
        return;
      }
      const pagos = await getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId))));
      const cobrados = ingresosDeAlquiler(pagos.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAlquileresCobrados([...cobrados].sort((a, b) => (b.receivedAt?.getTime?.() || 0) - (a.receivedAt?.getTime?.() || 0)));
    } catch (err) {
      console.warn("[IngresosEdificio] no se pudieron leer los alquileres del edificio:", err?.code || err);
    }
  };

  const contratosActivos = contratos.filter((c) => c.active !== false);
  const alertasContratos = contratosActivos.flatMap((c) =>
    alertasDeAlquiler(c).map((a) => `Unidad ${c.apartmentNumber || c.apartmentId}: ${a}`)
  );

  const fetchIncomes = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingId))));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setIncomes(list);
    } catch (err) {
      console.error("Error fetching incomes:", err);
      showToast("Error al cargar los ingresos", "error");
    } finally {
      setLoading(false);
    }
  };

  const uploadReceipt = async (file) => {
    if (!file) return null;
    const fileRef = ref(storage, `income_receipts/${buildingId}/${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  const handleCreate = async () => {
    if (!form.concept.trim() || !form.amount) {
      showToast("Completá concepto y monto", "error");
      return;
    }
    const amount = Number(form.amount);
    if (amount <= 0) {
      showToast("El monto debe ser mayor a 0", "error");
      return;
    }
    if (form.type === "alquiler" && contratosActivos.length > 0) {
      const unidades = contratosActivos.map((c) => c.apartmentNumber || c.apartmentId).join(", ");
      const ok = await confirmDialog(
        `Este edificio ya cobra alquiler con contrato por Cobranza (unidad ${unidades}) y lo cobrado entra solo como ingreso. Si este ingreso es ese mismo alquiler, se contaría dos veces. ¿Registrarlo igual?`,
        { title: "Alquiler con contrato", confirmLabel: "Registrar igual", danger: true }
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      let receiptUrl = null;
      if (form.receipt) receiptUrl = await uploadReceipt(form.receipt);

      const received = form.alreadyReceived;
      await addDoc(collection(db, "buildingincomes"), {
        buildingid: Number(buildingId),
        concept: form.concept.trim(),
        type: form.type,
        counterparty: form.counterparty.trim() || null,
        amount,
        currency: form.currency || "UYU",
        targetFundId: form.targetFundId || "caja",
        date: form.date,
        period: { month: Number(form.month), year: Number(form.year) },
        recurring: !!form.recurring,
        status: received ? "cobrado" : "pendiente",
        receivedAt: received ? form.date : null,
        receiptUrl,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser?.email || "",
        annulledAt: null,
        annulledReason: null,
      });
      showToast("Ingreso registrado", "success");
      setShowForm(false);
      setForm(emptyForm);
      fetchIncomes();
    } catch (err) {
      console.error("Error creating income:", err);
      showToast("Error al registrar el ingreso", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    const amount = Number(editModal.amount);
    if (!editModal.concept?.trim() || amount <= 0) {
      showToast("Completá concepto y monto (mayor a 0)", "error");
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "buildingincomes", editModal.id), {
        concept: editModal.concept.trim(),
        type: editModal.type,
        counterparty: editModal.counterparty?.trim() || null,
        amount,
        currency: editModal.currency || "UYU",
        targetFundId: editModal.targetFundId || "caja",
        date: editModal.date,
        recurring: !!editModal.recurring,
        updatedAt: Timestamp.now(),
        updatedBy: auth.currentUser?.email || "",
      });
      showToast("Ingreso actualizado", "success");
      setEditModal(null);
      fetchIncomes();
    } catch (err) {
      console.error("Error editing income:", err);
      showToast("Error al actualizar el ingreso", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCobrar = async () => {
    if (!cobrarModal) return;
    const dateStr = cobrarDate || new Date().toISOString().split("T")[0];
    try {
      await updateDoc(doc(db, "buildingincomes", cobrarModal.id), {
        status: "cobrado",
        receivedAt: dateStr,
        date: dateStr,
      });
      showToast("Ingreso marcado como cobrado", "success");
      setCobrarModal(null);
      setCobrarDate("");
      fetchIncomes();
    } catch (err) {
      console.error("Error marking income cobrado:", err);
      showToast("Error al actualizar el ingreso", "error");
    }
  };

  const handleAnnul = async () => {
    if (!annulReason.trim()) {
      showToast("Ingresá el motivo de anulación", "error");
      return;
    }
    try {
      await updateDoc(doc(db, "buildingincomes", annulModal.id), {
        status: "anulado",
        annulledAt: Timestamp.now(),
        annulledBy: auth.currentUser?.email || "",
        annulledReason: annulReason.trim(),
      });
      showToast("Ingreso anulado", "success");
      setAnnulModal(null);
      setAnnulReason("");
      fetchIncomes();
    } catch (err) {
      console.error("Error annulling income:", err);
      showToast("Error al anular el ingreso", "error");
    }
  };

  const filtered = incomes.filter((inc) => {
    if (filter !== "todos" && inc.status !== filter) return false;
    if (search.trim()) {
      const s = search.toLowerCase();
      return (
        (inc.concept || "").toLowerCase().includes(s) ||
        (inc.counterparty || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const cobradosUYU = incomes.filter((i) => i.status === "cobrado" && (i.currency || "UYU") !== "USD");
  const cobradosUSD = incomes.filter((i) => i.status === "cobrado" && (i.currency || "UYU") === "USD");
  const stats = {
    total: incomes.length,
    pendientes: incomes.filter((i) => i.status === "pendiente").length,
    montoUYU: cobradosUYU.reduce((s, i) => s + (Number(i.amount) || 0), 0),
    montoUSD: cobradosUSD.reduce((s, i) => s + (Number(i.amount) || 0), 0),
  };

  const fundName = (fundId) =>
    getAllActiveFunds(selectedBuilding).find((f) => f.id === (fundId || "caja"))?.name || "Caja";
  const typeLabel = (t) => INCOME_TYPES.find((x) => x.id === t)?.label || "Otro";
  const statusLabel = { pendiente: "Pendiente", cobrado: "Cobrado", anulado: "Anulado" };
  const statusVariant = { pendiente: "warning", cobrado: "success", anulado: "neutral" };
  const fmtAmount = (inc) => (inc.currency || "UYU") === "USD" ? formatCurrencyUSD(Number(inc.amount)) : formatCurrencyUY(Number(inc.amount));
  const formatDate = (val) => {
    if (!val) return "-";
    const d = typeof val === "string" ? new Date(val + "T12:00:00") : (val.toDate ? val.toDate() : new Date(val));
    return d.toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <FaExclamationCircle size={48} color={colors.gray400} />
          <p style={{ color: colors.gray600, marginTop: 16 }}>Seleccioná un edificio para ver los ingresos</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>
            <FaHandHoldingUsd style={{ marginRight: 8 }} /> Ingresos del edificio
          </h2>
          <p style={styles.subtitle}>
            Ingresos que no son gastos comunes (cartel publicitario, antenas, alquileres sin contrato…). Cada uno suma al fondo elegido. Las unidades "Alquiler del edificio" con contrato se cobran solas en el cierre y aparecen abajo.
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={() => { setForm(emptyForm); setShowForm(true); }}>
          <FaPlus size={14} /> Nuevo ingreso
        </button>
      </div>

      <div style={styles.kpiRow}>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{stats.total}</div><div style={styles.kpiLabel}>Total</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{stats.pendientes}</div><div style={styles.kpiLabel}>Pendientes</div></div>
        <div style={styles.kpiCard}><div style={styles.kpiValue}>{formatCurrencyUY(stats.montoUYU)}</div><div style={styles.kpiLabel}>Cobrado (UYU)</div></div>
        {stats.montoUSD > 0 && (
          <div style={styles.kpiCard}><div style={styles.kpiValue}>{formatCurrencyUSD(stats.montoUSD)}</div><div style={styles.kpiLabel}>Cobrado (USD)</div></div>
        )}
      </div>

      {alertasContratos.length > 0 && (
        <div role="status" style={styles.alertaAlquiler}>
          <FaExclamationCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{alertasContratos.map((a) => <div key={a}>{a}</div>)}</div>
        </div>
      )}

      <div style={styles.filterRow}>
        <div style={styles.searchBox}>
          <FaSearch size={14} color={colors.gray400} />
          <input style={styles.searchInput} placeholder="Buscar por concepto o contraparte" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={styles.filterTabs}>
          {[
            { id: "todos", label: "Todos" },
            { id: "pendiente", label: "Pendientes" },
            { id: "cobrado", label: "Cobrados" },
            { id: "anulado", label: "Anulados" },
          ].map((t) => (
            <button key={t.id} style={{ ...styles.filterTab, ...(filter === t.id ? styles.filterTabActive : {}) }} onClick={() => setFilter(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingState message="Cargando ingresos..." />
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <FaHandHoldingUsd size={40} color={colors.gray300} />
          <p style={{ color: colors.gray500, marginTop: 12 }}>No hay ingresos para mostrar</p>
        </div>
      ) : (
        <div style={styles.list}>
          {filtered.map((inc) => (
            <div key={inc.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardLeft}>
                  <span style={styles.typeBadge}>{typeLabel(inc.type)}</span>
                  <StatusBadge variant={statusVariant[inc.status] || "neutral"}>{statusLabel[inc.status] || inc.status}</StatusBadge>
                  {inc.status !== "anulado" && (
                    <span style={styles.reserveBadge} title={`Fondo: ${fundName(inc.targetFundId)}`}>
                      <FaMoneyBillWave size={10} /> {fundName(inc.targetFundId)}
                    </span>
                  )}
                  {inc.recurring && (
                    <span style={{ ...styles.reserveBadge, color: colors.info, background: colors.infoLight }} title="Ingreso recurrente">
                      <FaSyncAlt size={10} /> Recurrente
                    </span>
                  )}
                </div>
                <div style={styles.cardAmount}>{fmtAmount(inc)}</div>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.cardRow}><span style={styles.cardText}>{inc.concept}</span></div>
                {inc.counterparty && <div style={styles.cardRow}><span style={styles.cardSubtext}>Contraparte: {inc.counterparty}</span></div>}
                <div style={styles.cardRow}>
                  <span style={styles.cardSubtext}>
                    {inc.status === "cobrado" ? `Cobrado ${formatDate(inc.receivedAt || inc.date)}` : `Fecha ${formatDate(inc.date)}`}
                    {inc.period ? ` · ${MONTHS[(inc.period.month || 1) - 1]} ${inc.period.year || ""}` : ""}
                  </span>
                  {inc.receiptUrl && (
                    <a href={inc.receiptUrl} target="_blank" rel="noreferrer" style={{ ...styles.cardSubtext, color: colors.primary, display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <FaPaperclip size={11} /> Comprobante
                    </a>
                  )}
                </div>
                {inc.status === "anulado" && inc.annulledReason && (
                  <div style={styles.cardRow}><span style={styles.cardSubtext}>Anulado: {inc.annulledReason}</span></div>
                )}
              </div>
              {inc.status !== "anulado" && (
                <div style={styles.cardActions}>
                  {inc.status === "pendiente" && (
                    <button style={styles.btnSuccess} onClick={() => { setCobrarModal(inc); setCobrarDate(inc.date || new Date().toISOString().split("T")[0]); }}>
                      <FaCheck size={12} /> Marcar cobrado
                    </button>
                  )}
                  <button style={styles.btnSecondary} onClick={() => setEditModal({ ...inc })}>
                    <FaPen size={12} /> Editar
                  </button>
                  <button style={styles.btnDanger} onClick={() => setAnnulModal(inc)}>
                    <FaBan size={12} /> Anular
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {alquileresCobrados.length > 0 && (filter === "todos" || filter === "cobrado") && (
        <div style={{ marginTop: spacing[5] }}>
          <h3 style={styles.seccionTitulo}>Alquileres cobrados por Cobranza</h3>
          <p style={styles.seccionTexto}>
            Se registran solos cuando el inquilino paga el alquiler en Cobranza: no hace falta cargarlos como ingreso manual.
          </p>
          <div style={styles.list}>
            {alquileresCobrados.map((inc) => (
              <div key={inc.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardLeft}>
                    <span style={styles.typeBadge}>Alquiler del edificio</span>
                    <StatusBadge status="pagado" label="Cobrado por Cobranza" size="sm" />
                    <span style={styles.reserveBadge} title={`Fondo: ${fundName(inc.targetFundId)}`}>
                      <FaMoneyBillWave size={10} /> {fundName(inc.targetFundId)}
                    </span>
                  </div>
                  <div style={styles.cardAmount}>{formatCurrencyUY(Number(inc.amount))}</div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.cardRow}><span style={styles.cardText}>{inc.concept}</span></div>
                  <div style={styles.cardRow}><span style={styles.cardSubtext}>Cobrado {formatDate(inc.receivedAt)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crear */}
      <AdminModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Nuevo ingreso del edificio"
        size="medium"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => setShowForm(false)}>Cancelar</button>
            <button style={styles.btnPrimary} onClick={handleCreate} disabled={saving}>{saving ? "Guardando..." : "Registrar ingreso"}</button>
          </>
        }
      >
        <div style={styles.formGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Tipo</label>
            <select style={styles.select} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {INCOME_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Contraparte</label>
            <input style={styles.input} placeholder="Ej: Empresa de publicidad" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} />
          </div>
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <label style={styles.label}>Concepto *</label>
            <input style={styles.input} placeholder="Ej: Cartel medianera enero" value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Monto *</label>
            <input type="number" min="0" step="0.01" style={styles.input} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Moneda</label>
            <select style={styles.select} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="UYU">Pesos (UYU)</option>
              <option value="USD">Dólares (USD)</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Fondo destino</label>
            <select style={styles.select} value={form.targetFundId} onChange={(e) => setForm({ ...form, targetFundId: e.target.value })}>
              {getAllActiveFunds(selectedBuilding).map((fund) => <option key={fund.id} value={fund.id}>{fund.name}</option>)}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Fecha</label>
            <input type="date" style={styles.input} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Período (mes / año)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select style={{ ...styles.select, flex: 1 }} value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <input type="number" style={{ ...styles.input, width: 90 }} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
          </div>
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={form.alreadyReceived} onChange={(e) => setForm({ ...form, alreadyReceived: e.target.checked })} />
              Ya fue cobrado (impacta el saldo del fondo). Si no, queda pendiente.
            </label>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
              Ingreso recurrente (mensual)
            </label>
          </div>
          <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
            <label style={styles.label}>Comprobante (opcional)</label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setForm({ ...form, receipt: e.target.files?.[0] || null })} />
          </div>
        </div>
      </AdminModal>

      {/* Editar */}
      <AdminModal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title="Editar ingreso"
        size="medium"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => setEditModal(null)}>Cancelar</button>
            <button style={styles.btnPrimary} onClick={handleSaveEdit} disabled={saving}>{saving ? "Guardando..." : "Guardar cambios"}</button>
          </>
        }
      >
        {editModal && (
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Tipo</label>
              <select style={styles.select} value={editModal.type} onChange={(e) => setEditModal({ ...editModal, type: e.target.value })}>
                {INCOME_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Contraparte</label>
              <input style={styles.input} value={editModal.counterparty || ""} onChange={(e) => setEditModal({ ...editModal, counterparty: e.target.value })} />
            </div>
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <label style={styles.label}>Concepto *</label>
              <input style={styles.input} value={editModal.concept || ""} onChange={(e) => setEditModal({ ...editModal, concept: e.target.value })} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Monto *</label>
              <input type="number" min="0" step="0.01" style={styles.input} value={editModal.amount} onChange={(e) => setEditModal({ ...editModal, amount: e.target.value })} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Moneda</label>
              <select style={styles.select} value={editModal.currency || "UYU"} onChange={(e) => setEditModal({ ...editModal, currency: e.target.value })}>
                <option value="UYU">Pesos (UYU)</option>
                <option value="USD">Dólares (USD)</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Fondo destino</label>
              <select style={styles.select} value={editModal.targetFundId || "caja"} onChange={(e) => setEditModal({ ...editModal, targetFundId: e.target.value })}>
                {getAllActiveFunds(selectedBuilding).map((fund) => <option key={fund.id} value={fund.id}>{fund.name}</option>)}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Fecha</label>
              <input type="date" style={styles.input} value={editModal.date || ""} onChange={(e) => setEditModal({ ...editModal, date: e.target.value })} />
            </div>
            <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" checked={!!editModal.recurring} onChange={(e) => setEditModal({ ...editModal, recurring: e.target.checked })} />
                Ingreso recurrente (mensual)
              </label>
            </div>
          </div>
        )}
      </AdminModal>

      {/* Cobrar */}
      <AdminModal
        open={!!cobrarModal}
        onClose={() => { setCobrarModal(null); setCobrarDate(""); }}
        title="Marcar como cobrado"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => { setCobrarModal(null); setCobrarDate(""); }}>Cancelar</button>
            <button style={styles.btnSuccess} onClick={handleCobrar}><FaCheck size={12} /> Confirmar</button>
          </>
        }
      >
        {cobrarModal && (
          <div>
            <p style={{ fontSize: typography.sizes.base, color: colors.gray900 }}>
              <strong>{cobrarModal.concept}</strong> — {fmtAmount(cobrarModal)}
            </p>
            <div style={{ ...styles.formGroup, marginTop: 12 }}>
              <label style={styles.label}>Fecha de cobro</label>
              <input type="date" style={styles.input} value={cobrarDate} onChange={(e) => setCobrarDate(e.target.value)} />
            </div>
            <p style={{ marginTop: 8, color: colors.gray500, fontSize: typography.sizes.sm }}>
              Al confirmarse, el monto ingresa al fondo {fundName(cobrarModal.targetFundId)}.
            </p>
          </div>
        )}
      </AdminModal>

      {/* Anular */}
      <AdminModal
        open={!!annulModal}
        onClose={() => { setAnnulModal(null); setAnnulReason(""); }}
        title="Anular ingreso"
        size="narrow"
        footer={
          <>
            <button style={styles.btnSecondary} onClick={() => { setAnnulModal(null); setAnnulReason(""); }}>Cancelar</button>
            <button style={styles.btnDanger} onClick={handleAnnul}><FaBan size={12} /> Anular</button>
          </>
        }
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Motivo de anulación *</label>
          <textarea style={{ ...styles.input, minHeight: 80, resize: "vertical" }} placeholder="Ej: Registrado por error" value={annulReason} onChange={(e) => setAnnulReason(e.target.value)} />
        </div>
      </AdminModal>
    </div>
  );
};

const styles = {
  container: { padding: spacing[6], fontFamily: typography.fontFamily },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: spacing[5], flexWrap: "wrap", gap: spacing[3] },
  title: { ...pageTitle, fontSize: typography.sizes.xl, display: "flex", alignItems: "center" },
  subtitle: { fontSize: typography.sizes.base, color: colors.gray600, margin: "4px 0 0", maxWidth: 720 },
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: spacing[3], marginBottom: spacing[5] },
  kpiCard: { background: colors.white, borderRadius: radius.md, padding: `${spacing[4]}px ${spacing[5]}px`, borderLeft: `4px solid ${colors.primary}`, boxShadow: shadows.sm },
  kpiValue: { fontSize: typography.sizes["2xl"], fontWeight: 400, color: colors.primary },
  kpiLabel: { fontSize: typography.sizes.sm, color: colors.gray600, marginTop: 2 },
  filterRow: { display: "flex", gap: spacing[3], marginBottom: spacing[4], flexWrap: "wrap", alignItems: "center" },
  searchBox: { display: "flex", alignItems: "center", gap: spacing[2], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: "6px 12px", flex: "1 1 220px", maxWidth: 320 },
  searchInput: { border: "none", outline: "none", fontSize: typography.sizes.base, flex: 1, background: "transparent", fontFamily: typography.fontFamily },
  filterTabs: { display: "flex", gap: spacing[1], flexWrap: "wrap" },
  filterTab: { padding: "6px 14px", borderRadius: radius.sm, border: `1px solid ${colors.gray200}`, background: colors.white, fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900, cursor: "pointer", transition: `all ${transitions.fast}` },
  filterTabActive: { background: colors.primary, color: colors.white, borderColor: colors.primary },
  list: { display: "flex", flexDirection: "column", gap: spacing[3] },
  card: { ...cardBase, padding: spacing[4] },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  cardLeft: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  typeBadge: { fontSize: typography.sizes.sm, fontWeight: 400, color: colors.black, background: colors.gray100, borderRadius: 6, padding: "2px 10px" },
  reserveBadge: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 400, color: colors.successDark, background: colors.successLight, borderRadius: radius.md, padding: "2px 8px" },
  cardAmount: { fontSize: typography.sizes.xl, fontWeight: 400, color: colors.black },
  cardBody: { display: "flex", flexDirection: "column", gap: 6 },
  cardRow: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  cardText: { fontSize: typography.sizes.base, color: colors.gray900 },
  cardSubtext: { fontSize: typography.sizes.sm, color: colors.gray400 },
  cardActions: { display: "flex", gap: spacing[2], marginTop: spacing[3], paddingTop: spacing[3], borderTop: `1px solid ${colors.gray100}` },
  emptyState: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px" },
  btnPrimary: { ...btnPrimary, padding: "8px 18px", fontSize: typography.sizes.base },
  btnSecondary: { ...btnSecondary, padding: "8px 18px", fontSize: typography.sizes.base },
  btnSuccess: { display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", background: colors.success, color: colors.white, border: "none", borderRadius: radius.sm, fontSize: typography.sizes.sm, fontWeight: 400, cursor: "pointer", transition: `all ${transitions.fast}` },
  btnDanger: { ...btnDanger, padding: "6px 14px", fontSize: typography.sizes.sm },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[4] },
  formGroup: { display: "flex", flexDirection: "column", gap: spacing[1] },
  label: { fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900 },
  input: { ...inputBase, width: "auto", padding: "8px 12px", fontSize: typography.sizes.base },
  select: { padding: "8px 12px", border: `1px solid ${colors.gray300}`, borderRadius: radius.sm, fontSize: typography.sizes.base, outline: "none", background: colors.white, fontFamily: typography.fontFamily },
  checkboxLabel: { display: "flex", alignItems: "center", gap: spacing[2], fontSize: typography.sizes.base, color: colors.gray900, cursor: "pointer" },
  alertaAlquiler: { display: "flex", gap: spacing[2], alignItems: "flex-start", padding: `${spacing[3]}px ${spacing[4]}px`, marginBottom: spacing[4], background: colors.warningLight, color: colors.warningDark, borderRadius: radius.sm, fontSize: typography.sizes.sm },
  seccionTitulo: { margin: `0 0 ${spacing[1]}px`, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black },
  seccionTexto: { margin: `0 0 ${spacing[3]}px`, fontSize: typography.sizes.sm, color: colors.gray600 },
};

export default IngresosEdificio;
